/**
 * Fachada da adequação de imagem para o WhatsApp oficial (Cloud API).
 *
 * Análoga a `utils/video/converterVideoParaMeta.ts`, e é o único ponto que o
 * `chatStore` precisa conhecer:
 *
 *   `limiteImagemMeta`  → formatos aceitos e teto deste canal
 *   `buscaDeCompressao` → menor compressão que cabe
 *   este arquivo        → decodifica e codifica no canvas do navegador
 *
 * Regra de ouro: imagem que **já cabe e já é JPEG/PNG sai intacta** —
 * reencodar à toa só degradaria a foto.
 */

import { formatoDeImagemAceito, formatoSemPerda, normalizarMimeDeImagem, regrasDeImagem } from './limiteImagemMeta';
import { buscarMenorCompressao, type Codificar, type FormatoDeSaida } from './buscaDeCompressao';
import { formatarBytes } from '@/utils/video/limiteVideoMeta';

/**
 * `convertendo-imagem`: troca de formato (webp → jpeg, etc.).
 * `comprimindo-imagem`: o formato já servia, só o tamanho não.
 */
export type EtapaDeImagem = 'convertendo-imagem' | 'comprimindo-imagem';

export interface OpcoesDeConversaoDeImagem {
    signal?: AbortSignal;
    onProgress?: (fracao: number) => void;
    onEtapa?: (etapa: EtapaDeImagem) => void;
}

export type ResultadoDeConversaoDeImagem =
    | { estado: 'intacto'; arquivo: File }
    | { estado: 'convertido'; arquivo: File }
    | { estado: 'impossivel'; motivo: string };

interface ImagemDecodificada {
    fonte: CanvasImageSource;
    largura: number;
    altura: number;
    liberar: () => void;
}

/**
 * `createImageBitmap` cobre jpeg/png/webp/gif/bmp/avif e já aplica a orientação
 * do EXIF; `<img>` é o fallback para o que ele recusa (SVG, principalmente).
 * HEIC só o Safari decodifica — nos demais os dois falham, e isso vira
 * `impossivel` com a frase pronta.
 */
async function decodificar(arquivo: Blob): Promise<ImagemDecodificada> {
    try {
        const bitmap = await createImageBitmap(arquivo);
        return { fonte: bitmap, largura: bitmap.width, altura: bitmap.height, liberar: () => bitmap.close() };
    } catch {
        const url = URL.createObjectURL(arquivo);
        try {
            const img = new Image();
            img.src = url;
            await img.decode();
            if (!img.naturalWidth || !img.naturalHeight) throw new Error('imagem sem dimensões');
            return { fonte: img, largura: img.naturalWidth, altura: img.naturalHeight, liberar: () => URL.revokeObjectURL(url) };
        } catch (erro) {
            URL.revokeObjectURL(url);
            throw erro;
        }
    }
}

type Tela = OffscreenCanvas | HTMLCanvasElement;

function criarTela(largura: number, altura: number): Tela {
    if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(largura, altura);
    const canvas = document.createElement('canvas');
    canvas.width = largura;
    canvas.height = altura;
    return canvas;
}

function contexto2d(tela: Tela) {
    const ctx = tela.getContext('2d') as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
    if (!ctx) throw new Error('Canvas 2D indisponível neste navegador.');
    return ctx;
}

function telaParaBlob(tela: Tela, formato: FormatoDeSaida, qualidade?: number): Promise<Blob> {
    if ('convertToBlob' in tela) return tela.convertToBlob({ type: formato, quality: qualidade });
    return new Promise((resolve, reject) => {
        tela.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Falha ao codificar a imagem.'))), formato, qualidade);
    });
}

/**
 * Algum pixel não-opaco? Só faz sentido em formato que carrega alfa — JPEG
 * nunca tem, e varrer 12 MP à toa custa.
 */
function temTransparencia(img: ImagemDecodificada, mime: string): boolean {
    if (mime === 'image/jpeg') return false;
    const tela = criarTela(img.largura, img.altura);
    const ctx = contexto2d(tela);
    ctx.drawImage(img.fonte, 0, 0);
    const { data } = ctx.getImageData(0, 0, img.largura, img.altura);
    for (let i = 3; i < data.length; i += 4) if (data[i] < 255) return true;
    return false;
}

function codificadorDe(img: ImagemDecodificada): Codificar {
    return async ({ formato, escala, qualidade }) => {
        const largura = Math.max(1, Math.round(img.largura * escala));
        const altura = Math.max(1, Math.round(img.altura * escala));
        const tela = criarTela(largura, altura);
        const ctx = contexto2d(tela);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        // JPEG não tem alfa: sem fundo, o transparente vira PRETO.
        if (formato === 'image/jpeg') {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, largura, altura);
        }
        ctx.drawImage(img.fonte, 0, 0, largura, altura);
        return telaParaBlob(tela, formato, qualidade);
    };
}

function trocarExtensao(nome: string, formato: FormatoDeSaida): string {
    const extensao = formato === 'image/png' ? '.png' : '.jpg';
    const base = nome.replace(/\.[^./\\]+$/, '');
    return (base || 'imagem') + extensao;
}

/**
 * Devolve o arquivo pronto para subir ao storage — o original, quando já
 * serve, ou um JPEG/PNG que cabe no teto do canal.
 *
 * Não lança por imagem impossível (formato que o navegador não decodifica,
 * ou que não cabe nem reduzida): isso é `{ estado: 'impossivel' }`, com motivo
 * pronto para mostrar. Lança só no cancelamento (`AbortError`).
 */
export async function converterImagemParaMeta(
    arquivo: File,
    plataforma: string | null | undefined,
    opts: OpcoesDeConversaoDeImagem = {},
): Promise<ResultadoDeConversaoDeImagem> {
    const regras = regrasDeImagem(plataforma);
    // Canal fora da Cloud API: outro transporte, outras regras.
    if (!regras) return { estado: 'intacto', arquivo };

    const mime = normalizarMimeDeImagem(arquivo.type);
    const formatoServe = formatoDeImagemAceito(regras, mime);
    if (formatoServe && arquivo.size <= regras.limiteBytes) return { estado: 'intacto', arquivo };

    // Só aqui o cartão aparece: imagem que passa intacta — o caso comum — não
    // pisca nada na tela.
    opts.onEtapa?.(formatoServe ? 'comprimindo-imagem' : 'convertendo-imagem');

    let img: ImagemDecodificada;
    try {
        img = await decodificar(arquivo);
    } catch {
        return {
            estado: 'impossivel',
            motivo:
                `O WhatsApp oficial só aceita imagens JPEG ou PNG, e este navegador não conseguiu ` +
                `abrir "${arquivo.name}" (${mime || 'formato desconhecido'}) para convertê-la. ` +
                `Salve a imagem como JPEG ou PNG e envie de novo.`,
        };
    }

    try {
        const resultado = await buscarMenorCompressao({
            limiteBytes: regras.limiteBytes,
            temTransparencia: temTransparencia(img, mime),
            tentarPngCheio: !formatoServe && formatoSemPerda(mime),
            codificar: codificadorDe(img),
            signal: opts.signal,
            onProgress: opts.onProgress,
        });

        if (resultado.estado === 'impossivel') {
            return {
                estado: 'impossivel',
                motivo:
                    `Esta imagem tem ${formatarBytes(arquivo.size)} e não coube no limite de ` +
                    `${formatarBytes(regras.limiteBytes)} do WhatsApp oficial nem reduzida.`,
            };
        }

        const { blob, tentativa } = resultado;
        return {
            estado: 'convertido',
            arquivo: new File([blob], trocarExtensao(arquivo.name, tentativa.formato), {
                type: tentativa.formato,
                lastModified: Date.now(),
            }),
        };
    } finally {
        img.liberar();
    }
}
